import React from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { themeColor, customColor } from "../../../../types/color";
import {
  ShareConversationDialogWidgetProps,
  ShareConversationDialogWidgetState,
} from "./types";

const defaultState: ShareConversationDialogWidgetState = {
  dialogBgColor: themeColor("secondary"),
  dialogBorderRadius: 16,
  dialogPadding: 40,
  titleFontSize: 24,
  titleColor: themeColor("textPrimary"),
  descriptionFontSize: 16,
  descriptionColor: themeColor("textPrimary"),
  logoIcon: "default",
  logoContainerSize: 64,
  logoContainerBgColor: customColor("#f5f5f5"),
  primaryButtonBgColor: themeColor("textPrimary"),
  primaryButtonTextColor: themeColor("secondary"),
  primaryButtonBorderRadius: 999,
  cancelTextColor: themeColor("textPrimary"),
};

const editFields: EditField[] = [
  // ── Dialog ────────────────────────────────────────────────────────────
  { key: "dialogBgColor", label: "Background", type: "color", group: "Dialog" },
  {
    key: "dialogBorderRadius",
    label: "Border Radius",
    type: "number",
    min: 0,
    max: 32,
    step: 1,
    group: "Dialog",
  },
  {
    key: "dialogPadding",
    label: "Padding",
    type: "number",
    min: 16,
    max: 60,
    step: 4,
    group: "Dialog",
  },

  // ── Logo ──────────────────────────────────────────────────────────────
  { key: "logoIcon", label: "Logo Icon", type: "icon", group: "Logo" },
  { key: "logoContainerSize", label: "Icon Size", type: "number", min: 32, max: 96, step: 4, group: "Logo" },
  { key: "logoContainerBgColor", label: "Background", type: "color", group: "Logo" },

  // ── Title ─────────────────────────────────────────────────────────────
  { key: "titleFontSize", label: "Font Size", type: "number", min: 16, max: 32, step: 1, group: "Title" },
  { key: "titleColor", label: "Color", type: "color", group: "Title" },

  // ── Description ───────────────────────────────────────────────────────
  { key: "descriptionFontSize", label: "Font Size", type: "number", min: 12, max: 20, step: 1, group: "Description" },
  { key: "descriptionColor", label: "Color", type: "color", group: "Description" },

  // ── Button ────────────────────────────────────────────────────────────
  { key: "primaryButtonBgColor", label: "Background", type: "color", group: "Button" },
  { key: "primaryButtonTextColor", label: "Text Color", type: "color", group: "Button" },
  {
    key: "primaryButtonBorderRadius",
    label: "Border Radius",
    type: "number",
    min: 0,
    max: 999,
    step: 1,
    group: "Button",
  },
  { key: "cancelTextColor", label: "Cancel Text Color", type: "color", group: "Button" },
];

export const ShareConversationDialogWidget: React.FC<
  ShareConversationDialogWidgetProps
> = ({ initialState = {}, widgetId, displayName, children }) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    themeColors,
    themeIcons,
    resolveColor,
    canSave,
  } = useWidgetEditable<ShareConversationDialogWidgetState>(
    widgetId,
    { ...defaultState, ...initialState },
    editFields
  );

  const logoCustomIcon =
    state.logoIcon && state.logoIcon !== "default"
      ? themeIcons[state.logoIcon]
      : null;
  const resolvedLogoUrl = logoCustomIcon ? logoCustomIcon.url : null;

  if (isLoading) {
    return <></>;
  }

  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
      themeIcons={themeIcons}
      triggerMode="persistent"
      triggerPosition="top-right"
      wrapperStyle={{ width: "100%", maxWidth: "28rem" }}
    >
      {children(state, resolveColor, resolvedLogoUrl)}
    </WidgetWrapper>
  );
};
